import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, HeartHandshake, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/donations`;
export const FUND_NAMES = ["Sponsor a Child", "Education and Nutrition Support", "General Funds"];
const PRESETS = [500, 1000, 2500, 5000, 15000];

const loadCheckout = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="block font-mono text-xs tracking-[0.15em] uppercase text-[#8A4B22] mb-2">{label}</span>
    <input {...props} className="w-full rounded-2xl border border-[#0F1D2F]/15 bg-white px-4 py-3 text-[#0F1D2F] focus:outline-none focus:border-[#CF5C0F] focus:ring-2 focus:ring-[#CF5C0F]/20" />
  </label>
);

export const DonateForm = ({ fund, setFund }) => {
  const [amount, setAmount] = useState(1000);
  const [form, setForm] = useState({ name: "", email: "", phone: "", pan: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (fund === "Education and Nutrition Support") setAmount(15000);
  }, [fund]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!amount || amount < 1) return setError("Please enter an amount.");
    setBusy(true);
    try {
      const ok = await loadCheckout();
      if (!ok) throw new Error("Could not load the payment window. Check your connection and try again.");
      const { data } = await axios.post(`${API}/order`, { fund, amount: Number(amount), ...form, pan: form.pan || null });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "Wings of Fire Foundation",
        description: fund,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#CF5C0F" },
        handler: async (res) => {
          try {
            const v = await axios.post(`${API}/verify`, res);
            setDone(v.data);
          } catch (err) {
            setError(err?.response?.data?.detail ?? "We could not confirm your payment. Please contact us.");
          }
          setBusy(false);
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (r) => setError(r?.error?.description ?? "Payment failed. Please try again."));
      rzp.open();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : err?.message ?? "Something went wrong.");
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="donation-success" className="rounded-[28px] bg-[#1B3824] text-white p-10 text-center">
        <CheckCircle2 className="w-14 h-14 mx-auto text-[#F2B36B]" />
        <h3 className="font-serif text-4xl mt-6">Thank you, {done.name}!</h3>
        <p className="mt-4 text-[#E2E8E4] text-lg">Your donation of ₹{Number(done.amount).toLocaleString("en-IN")} to {done.fund} was received.</p>
        <p className="mt-2 text-sm text-[#C9D6CD]">Payment ID: <span data-testid="donation-payment-id">{done.payment_id}</span></p>
      </div>
    );
  }

  return (
    <form id="donate-form" data-testid="donate-form" onSubmit={submit} className="rounded-[28px] bg-white border border-[#0F1D2F]/10 p-6 sm:p-10 shadow-[0_30px_60px_-30px_rgba(15,29,47,0.25)]">
      <div className="flex items-center gap-3">
        <HeartHandshake className="w-7 h-7 text-[#CF5C0F]" />
        <h3 className="font-serif text-3xl sm:text-4xl text-[#0F1D2F]">Donate online</h3>
      </div>
      <p className="mt-2 text-[#3A4756]">Pay securely by UPI, card, net banking or wallet.</p>

      <p className="font-mono text-xs tracking-[0.15em] uppercase text-[#8A4B22] mt-8 mb-3">Fund</p>
      <div className="flex flex-wrap gap-2">
        {FUND_NAMES.map((f, i) => (
          <button type="button" key={f} data-testid={`fund-option-${i}`} onClick={() => setFund(f)} className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${fund === f ? "bg-[#0F1D2F] text-white border-[#0F1D2F]" : "border-[#0F1D2F]/15 text-[#0F1D2F] hover:border-[#CF5C0F]"}`}>
            {f}
          </button>
        ))}
      </div>

      <p className="font-mono text-xs tracking-[0.15em] uppercase text-[#8A4B22] mt-8 mb-3">Amount (₹)</p>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button type="button" key={p} data-testid={`amount-preset-${p}`} onClick={() => setAmount(p)} className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${Number(amount) === p ? "bg-[#CF5C0F] text-white border-[#CF5C0F]" : "border-[#0F1D2F]/15 text-[#0F1D2F] hover:border-[#CF5C0F]"}`}>
            ₹{p.toLocaleString("en-IN")}
          </button>
        ))}
        <input data-testid="amount-custom-input" type="number" min="1" max="500000" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Custom amount" className="w-32 rounded-full border border-[#0F1D2F]/15 px-4 py-2 text-sm focus:outline-none focus:border-[#CF5C0F]" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <Field label="Full name*" data-testid="donor-name-input" required value={form.name} onChange={set("name")} />
        <Field label="Email*" data-testid="donor-email-input" type="email" required value={form.email} onChange={set("email")} />
        <Field label="Phone*" data-testid="donor-phone-input" type="tel" required minLength={7} value={form.phone} onChange={set("phone")} />
        <Field label="PAN (for 80G receipt)" data-testid="donor-pan-input" maxLength={10} value={form.pan} onChange={set("pan")} />
      </div>

      {error && <p data-testid="donate-error" className="mt-6 rounded-2xl bg-[#FDECEC] text-[#9B1C1C] px-4 py-3 text-sm">{error}</p>}

      <button type="submit" disabled={busy} data-testid="donate-submit-button" className="btn-fire btn-lg w-full justify-center mt-8 disabled:opacity-60">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
        Donate ₹{Number(amount || 0).toLocaleString("en-IN")}
      </button>
    </form>
  );
};
